import { Search } from "@mui/icons-material";
import { Box, Button, Chip, Menu, OutlinedInput, Typography } from "@mui/material";
import CustomCheckbox from "components/Checkbox";
import { useMemo, useState, useEffect, useCallback } from "react";
import { VariableSizeList as List } from "react-window";
import styles from "./hotelFilterMenu.module.css";

const DEFAULT_ROW_HEIGHT = 34;
const MAX_VISIBLE_ITEMS = 8;
const LIST_WIDTH = 220;

const HotelsFilterMenu = ({
  open,
  onClose,
  anchorEl,
  allHotels = [],
  allGroups = [],
  onApply,
  setSelectedHotelOrGroupType,
  selectedHotelOrGroupType,
  showGroups = true,
}) => {
  const [selectedHotels, setSelectedHotels] = useState(allHotels.map(h => ({ ...h, type: "HOTEL" })));
  const [selectedGroups, setSelectedGroups] = useState(allGroups.map(h => ({ ...h, type: "GROUP" })));
  const [selectedType, setSelectedType] = useState(selectedHotelOrGroupType);
  const [searchTerm, setSearchTerm] = useState("");

  const selected = useMemo(() => (
    showGroups
      ? selectedType === "hotels"
        ? selectedHotels
        : selectedGroups
      : selectedHotels
  ), [showGroups, selectedType, selectedHotels, selectedGroups]);

  useEffect(() => {
  if (open) {
    if (selectedHotels.some(s => s?.isSelected)) {
      setSelectedType("hotels");
    } else if (selectedGroups.some(s => s?.isSelected)) {
      setSelectedType("groups");
    }
  }
}, [open]);

  const isSelectAllChecked = useMemo(() => {
    if (selectedType === "hotels") {
      return selectedHotels.filter(item => item.isSelected).length === allHotels.length;
    } else if (selectedType === "groups") {
      return selectedGroups.filter(item => item.isSelected).length === allGroups.length;
    }
    return false;
  }, [selectedType, selectedHotels, selectedGroups, allHotels.length, allGroups.length]);

  const toggleSelectAll = () => {
    if (selectedType === "hotels") {
      const checked = selectedHotels.filter(item => item.isSelected).length !== allHotels.length;
      setSelectedHotels(selectedHotels.map(item => ({ ...item, isSelected: checked })));
    } else if (selectedType === "groups") {
      const checked = selectedGroups.filter(item => item.isSelected).length !== allGroups.length;
      setSelectedGroups(selectedGroups.map(item => ({ ...item, isSelected: checked })));
    }
  };

  const clearFilters = () => {
    if (selectedType === "hotels") {
      setSelectedHotels(selectedHotels.map(item => ({ ...item, isSelected: false })));
    } else if (selectedType === "groups") {
      setSelectedGroups(selectedGroups.map(item => ({ ...item, isSelected: false })));
    }
  };

  const filteredItems = useMemo(() => (
    selected.filter((hotel) =>
      [hotel?.name, hotel?.hotel_name, hotel?.hotel_id?.toString(), hotel?.id?.toString()]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  ), [selected, searchTerm]);

  // Dynamic row height calculation (can be customized per item)
  const getRowHeight = useCallback(
    (index) => {
      const item = filteredItems[index];
      const label = item?.label || item?.name || item?.hotel_name || "";
      return label.length > 30 ? DEFAULT_ROW_HEIGHT + 12 : DEFAULT_ROW_HEIGHT;
    },
    [filteredItems]
  );

  const handleCheckboxChange = (item) => {
    if (selectedType === "hotels") {
      setSelectedHotels(selectedHotels.map(i =>
        (i?.id ? i?.id === item.id : i?.hotel_id === item?.hotel_id)
          ? { ...i, isSelected: !i.isSelected }
          : i
      ));
    } else if (selectedType === "groups") {
      setSelectedGroups(selectedGroups.map(i =>
        i?.id === item.id
          ? { ...i, isSelected: !i.isSelected }
          : i
      ));
    }
  };

  // Virtualized row renderer
  const Row = ({ index, style }) => {
    const item = filteredItems[index];
    return (
      <li
        className={styles.rowItem}
        style={{ ...style, height: style.height }}
      >
        <CustomCheckbox
          fontSize="12px"
          size={"small"}
          checked={item?.isSelected}
          title={item?.label || item?.name || item?.hotel_name}
          onChange={() => handleCheckboxChange(item)}
        />
      </li>
    );
  };

  return (
    <>
      <Menu
        sx={{ padding: 0 }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            maxWidth: LIST_WIDTH + 38,
            minWidth: LIST_WIDTH,
          },
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
      >
        <div aria-disabled className={styles.menuContainer}>
          <div className={styles.selectHeader}>Select</div>
          <div className={styles.chipRow}>
            <div
              onClick={() => {
                setSelectedType("hotels");
                setSelectedHotelOrGroupType("hotels");
              }}
            >
              <Chip
                label="Hotels"
                sx={{
                  backgroundColor: selectedType === "hotels" ? "#61D5A9" : "none",
                  marginRight: "10px",
                  height: "20px",
                  width: "92px",
                }}
              />
            </div>
            {showGroups && (
              <div
                onClick={() => {
                  setSelectedType("groups");
                  setSelectedHotelOrGroupType("groups");
                }}
              >
                <Chip
                  label="Groups"
                  sx={{
                    backgroundColor: selectedType === "groups" ? "#61D5A9" : "none",
                    marginRight: "4px",
                    height: "20px",
                    width: "92px",
                  }}
                />
              </div>
            )}
          </div>
          <OutlinedInput
            sx={{
              borderRadius: "10px",
              borderColor: "#CADEDD",
              width: LIST_WIDTH,
              height: "28px",
              fontSize: "12px",
              fontWeight: "400",
              fontFamily: "inter",
            }}
            size="small"
            placeholder="Search"
            startAdornment={
              <Search sx={{ color: "#CADEDD", fontSize: "18px" }} />
            }
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div style={{ display: "flex", marginTop: "5px" }}>
            <div className={styles.listRow}>
              <div className={styles.selectAllRow} onClick={toggleSelectAll}>
                <span>
                  <CustomCheckbox
                    fontSize="10px"
                    size={"small"}
                    checked={isSelectAllChecked}
                    labelStyles={{ color: "#C4C4C4" }}
                    title={selectedType === "hotels" ? "Hotels" : "All groups"}
                  />
                </span>
              </div>
              <ul className={styles.listUl}>
                {filteredItems.length === 0 ? (
                  <li className={styles.emptyList}>
                    {searchTerm 
                      ? `No ${selectedType === "hotels" ? "hotels" : "groups"} found matching "${searchTerm}"`
                      : `No ${selectedType === "hotels" ? "hotels" : "groups"} available for this account`
                    }
                  </li>
                ) : (
                  <List
                    height={Math.min(filteredItems.length, MAX_VISIBLE_ITEMS) * DEFAULT_ROW_HEIGHT}
                    itemCount={filteredItems.length}
                    itemSize={getRowHeight}
                    width={"100%"}
                  >
                    {Row}
                  </List>
                )}
              </ul>
            </div>
          </div>
          <Box className={styles.footerBox}>
            <Typography
              variant="caption"
              sx={{
                color: "#C4C4C4",
                cursor: "pointer",
              }}
              onClick={clearFilters}
            >
              Clear Filters
            </Typography>
            <Button
              variant="outlined"
              sx={{
                color: "black",
                backgroundColor: "#61D5A9",
                borderRadius: "20px",
                fontSize: "10px",
                width: "100px",
                height: "22px",
                paddingInline: "8px",
              }}
              onClick={() => {
                onApply(selected.filter((item) => item.isSelected));
                setSelectedHotelOrGroupType(selectedType);
                onClose();
              }}
            >
              Apply
            </Button>
          </Box>
        </div>
      </Menu>
    </>
  );
};

export default HotelsFilterMenu;
